import {buildingGroup, roomGroup} from "../../../main";
import {Room} from "./models/room";
import {maxAllowedRoofSpan, RoofThicknessMulti, RoofThicknessSingle} from "../../../constants/flatRoofConstants";
import {HBEWallDefaultThickness} from "../../../globals";
import {CombinedRoom} from "./models/combinedRoom";
import {toFixedFloat} from "../../../utils/utils";
import {RoofOrientation} from "../../../constants/roofConstants";
import {Mesh, PlaneGeometry, Vector3} from "three";
import {findChildrenByName, setBox3FromObjectArray} from "../../../utils/threeUtils";
import {supportBeamMeshName, wallGroupName} from "../../../constants/nameConstants";
import {t} from "../../../../i18n";

export class RoofConfigCalculator {
    constructor() {
        this.minimalThickness = null;
        this.orientation = null;
        this.roofSections = null;

        this.rooms = this.getRooms();

        if (this.rooms.length === 0) {
            throw new Error(t("roof_closed_room"));
        }

        this.xValid = this.checkIfSpansWithinLimit("x");
        this.zValid = this.checkIfSpansWithinLimit("z");

        if (!this.xValid && !this.zValid) {
            //Wenn beide Orientierungen die maximale Spannweite überschreiten, kann kein Dach erzeugt werden.
            throw new Error(t("roof_span_exceeded", {span: maxAllowedRoofSpan.toFixed(2)}));
        }

        this.xRooms = this.sortRoomsByOrientation("x", [...this.rooms]);
        this.zRooms = this.sortRoomsByOrientation("z", [...this.rooms]);

        this.xRooms = this.combineRoomsByOrientation(this.xRooms, "x", true);
        this.zRooms = this.combineRoomsByOrientation(this.zRooms, "z", true);

        if (this.xValid) {
            this.xSpans = this.getSpansByOrientation("x", this.xRooms);
            this.xSpans.sort(this.sortSpanArray);
            this.xThickness = this.getMinimalRoofThickness(this.xSpans);
        }
        if (this.zValid) {
            this.zSpans = this.getSpansByOrientation("z", this.zRooms);
            this.zSpans.sort(this.sortSpanArray);
            this.zThickness = this.getMinimalRoofThickness(this.zSpans);
        }

        console.log("x: ", this.xThickness, "z: ", this.zThickness);

        this.setRoofConfig();

        return {thickness: this.minimalThickness / 1000, orientation: this.orientation, sections: this.roofSections};
    }

    /**
     * Zur Sortierung des Arrays mit Spannweiten und Einfeld oder Mehrfeld.
     * Wird erst nach Einfeld oder Mehrfeld sortiert.
     * Diese werden dann jeweils noch nach absteigender Spannweite sortiert.
     * @param a
     * @param b
     * @returns {number|number}
     */
    sortSpanArray(a, b) {
        if (a.multi !== b.multi) {
            return a.multi ? 1 : -1;
        }

        return b.span - a.span;
    }

    /**
     * Liest alle Räume aus der Szene aus und erstellt aus jedem ein Raum Objekt.
     * @returns {Room[]}
     */
    getRooms() {
        let rooms = [];

        for (let i = 0; i < roomGroup.children.length; i++) {
            let roomsObject = roomGroup.children[i];
            rooms.push(new Room(i + 1, roomsObject));
        }

        if (rooms.length === 0) {
            let shellRoom = this.roomFromShell();
            if (shellRoom !== null) {
                rooms.push(shellRoom);
            }
        }

        rooms = this.filterEmbeddedRooms(rooms);
        rooms = this.combineIntersectingRooms(rooms);

        return rooms;
    }

    /**
     * Vier Unterzüge schließen einen Raum, auch wenn die Raumnetze noch nicht geschrieben wurden.
     * Die Innenfläche ist die Außenbox minus einer Wandstärke je Seite. Die Spannweite ergänzt danach wieder eine Wandstärke.
     */
    roomFromShell() {
        let walls = findChildrenByName(buildingGroup, wallGroupName, false, true) || [];
        let beams = findChildrenByName(buildingGroup, supportBeamMeshName, false, true) || [];
        let shell = walls.concat(beams);
        if (shell.length < 4) {
            return null;
        }

        let bounds = setBox3FromObjectArray(shell);
        if (bounds.isEmpty()) {
            return null;
        }

        let size = bounds.getSize(new Vector3());
        let interiorX = size.x - (2 * HBEWallDefaultThickness);
        let interiorZ = size.z - (2 * HBEWallDefaultThickness);
        if (interiorX < 0.2 || interiorZ < 0.2) {
            return null;
        }

        let center = bounds.getCenter(new Vector3());
        let mesh = new Mesh(new PlaneGeometry(interiorX, interiorZ));
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.set(center.x, 0, center.z);
        mesh.updateMatrixWorld(true);
        return new Room(1, mesh);
    }

    /**
     * Filtert aus allen Räumen, diese heraus dessen Bounds in den Bounds eines anderen Raumes eingeschlossen sind.
     * Diese sind nicht relevant für die Spannweiten-Berechnung, da die Maße des größeren Raums entscheidend sind, sorgen aber für Einfeldträger Spannweiten, die das Ergebnis verfälschen.
     * @param rooms
     */
    filterEmbeddedRooms(rooms) {
        return rooms.filter((room1, index1) => {
            for (let index2 = 0; index2 < rooms.length; index2++) {
                if (index1 !== index2) {
                    let room2 = rooms[index2];
                    if (this.isBoundsIncluded(room1.bounds, room2.bounds)) {
                        return false;
                    }
                }
            }
            return true;
        });
    }

    combineIntersectingRooms(rooms) {
        const combinedRooms = [];

        // Array to keep track of rooms that have been combined
        const combinedIndices = new Set();

        // Iterate through each pair of rooms
        for (let i = 0; i < rooms.length; i++) {
            if (combinedIndices.has(i)) {
                continue; // Skip if this room has already been combined
            }

            const room1 = rooms[i];
            let combined = false;

            for (let j = i + 1; j < rooms.length; j++) {
                if (combinedIndices.has(j)) {
                    continue; // Skip if this room has already been combined
                }

                const room2 = rooms[j];
                // Check if the bounding boxes of the rooms intersect
                if (room1.canCombineWith(room2)) {
                    // Combine the rooms
                    room1.combineWith(room2);
                    combinedRooms.push(room1);
                    combinedIndices.add(j); // Mark room2 as combined
                    combined = true;
                }
            }

            // If the room couldn't be combined with any other room, add it to the combinedRooms array
            if (!combined) {
                combinedRooms.push(room1);
            }
        }

        return combinedRooms;
    }


    /**
     * Hilfsfunktion um zu prüfen, ob eine BoundingBox eine andere einschließt.
     * @param bounds1
     * @param bounds2
     * @returns {boolean}
     */
    isBoundsIncluded(bounds1, bounds2) {
        return bounds1.min.x >= bounds2.min.x &&
            bounds1.min.y >= bounds2.min.y &&
            bounds1.min.z >= bounds2.min.z &&
            bounds1.max.x <= bounds2.max.x &&
            bounds1.max.y <= bounds2.max.y &&
            bounds1.max.z <= bounds2.max.z;
    }


    /**
     * Prüft, ob die Spannweite eines Raums in 'orientation' unterhalb der maximalen Spannweite liegt.
     * Wenn die maximale Spannweite überschritten wird, ist diese Orientierung automatisch nicht mehr gültig für weitere Prüfungen.
     * @param orientation
     * @returns {boolean}
     */
    checkIfSpansWithinLimit(orientation) {
        for (let room of this.rooms) {
            if (room.spans[orientation] > maxAllowedRoofSpan) {
                return false;
            }
        }

        return true;
    }

    /**
     * Nutzt die gefundenen Räume und ihr Bounds, um Räume die zusammengefasst werden können zur Verwendung der Mehrfeldträgerlogik, zu kombinieren.
     * Als Resultat wird this.rooms so angepasst das sich eine verschachtelte Struktur von zusammengefassten Räumen ergibt.
     * Durch die Kombination von zwei Räumen entsteht ein CombinedRoom, welcher die ursprünglichen Räume ersetzt.
     * Der CombinedRoom wird auf weitere Kombinationsmöglichkeiten geprüft. Wenn dabei in eine andere Orientierung kombiniert wird erhält man ein CombinedRoom, welcher einen CombinedRoom und bspw. einen anderen Raum enthält.
     * Der innere CombinedRoom enthält dabei dann die beiden Räume, die am Anfang kombiniert wurden.
     * @returns {null}
     */
    combineRoomsByOrientation(rooms, orientation, lastIterationCombined) {
        for (let i = 0; i < rooms.length; i++) {
            for (let j = i + 1; j < rooms.length; j++) {
                const roomA = rooms[i]; //Room oder CombinedRoom Objekt
                const roomB = rooms[j]; //Room oder CombinedRoom Objekt

                let combinationDirection = this.areRoomsAdjacent(roomA, roomB);
                if (combinationDirection === orientation) {
                    //Kombinieren der zwei Räume in einen CombinedRoom
                    const combinedIdentifier = roomA.identifier + "_" + roomB.identifier;
                    const combinedRoom = new CombinedRoom(combinedIdentifier, roomA, roomB, combinationDirection);

                    //Die ursprünglichen Räume aus dem Array entfernen
                    rooms.splice(j, 1);
                    rooms.splice(i, 1);

                    //Neuen kombinieren Raum hinzufügen
                    rooms.push(combinedRoom);

                    //Suche erneut beginnen mit dem angepassten Array
                    rooms = this.sortRoomsByOrientation(orientation, rooms); //Sortieren nach Orientierung damit sich nicht separate Gruppen bilden, sondern der CombinedRoom erweitert wird, wenn möglich
                    this.combineRoomsByOrientation(rooms, orientation, true);
                    //Beenden nach dem Kombinieren eines Raums. Weitere werden in der Rekursion gefunden. Wenn die Rekursion keine weiteren findet, sind wir fertig. Dann wird hier der Loop beendet.
                    return rooms;
                }
            }
        }

        if (lastIterationCombined) {
            rooms = this.sortRoomsByOrientation(orientation === "x" ? "z" : "x", rooms);
            this.combineRoomsByOrientation(rooms, orientation === "x" ? "z" : "x", false);
        }

        return rooms;
    }

    /**
     * Prüft, ob die zwei übergebenen Räume nebeneinander liegen und dieselbe Breite an der Stelle haben wo sie aneinanderstoßen.
     * Nur wenn beide Bedingungen erfüllt sind dürfen die Räume kombiniert werden.
     * @param roomA
     * @param roomB
     * @returns {string|null}
     */
    areRoomsAdjacent(roomA, roomB) {
        const xDistance1 = toFixedFloat(Math.abs(roomA.bounds.min.x - roomB.bounds.max.x));
        const xDistance2 = toFixedFloat(Math.abs(roomA.bounds.max.x - roomB.bounds.min.x));
        const zDistance1 = toFixedFloat(Math.abs(roomA.bounds.min.z - roomB.bounds.max.z));
        const zDistance2 = toFixedFloat(Math.abs(roomA.bounds.max.z - roomB.bounds.min.z));

        //Prüfen, ob die Räume die gleichen min und max x-Werte haben
        if (roomA.bounds.min.x === roomB.bounds.min.x && roomA.bounds.max.x === roomB.bounds.max.x) {
            //Prüfen, ob der Abstand in z-Richtung <= einer Wandstärke ist.
            if (zDistance1 <= HBEWallDefaultThickness || zDistance2 <= HBEWallDefaultThickness) {
                return "z"; //Erweitern sich in z-Richtung
            }
        }

        //Prüfen, ob die Räume die gleichen min und max z-Werte haben
        if (roomA.bounds.min.z === roomB.bounds.min.z && roomA.bounds.max.z === roomB.bounds.max.z) {
            //Prüfen, ob der Abstand in x-Richtung <= einer Wandstärke ist.
            if (xDistance1 <= HBEWallDefaultThickness || xDistance2 <= HBEWallDefaultThickness) {
                return "x"; //Erweitern sich in z-Richtung
            }
        }

        return null;
    }

    /**
     * Liest die Spannweiten und Einfeld/Mehrfeldinformation aus den gefundenen Räumen aus.
     * @param orientation
     * @param rooms
     * @param parentMulti - Wenn der parent ein CombinedRoom ist zählen die enthaltenen Spannweiten als Mehrfeldträger
     * @returns {{multi, span}[]}
     */
    getSpansByOrientation(orientation, rooms, parentMulti = false) {
        let spans = [];

        for (let room of rooms) {
            let span = room.spans[orientation];
            //Ist nur Mehrfeldträger, wenn es ein CombinedRoom ist dessen combinationDirection der Richtung des Dachs entspricht
            let isMulti = (room instanceof CombinedRoom && orientation === room.combinationDirection) || parentMulti;

            //Rekursion durch verschachtelte Räume
            if (room instanceof CombinedRoom) {
                for (let innerRoom of room.rooms) {
                    /*spans.push({
                        multi: innerRoom instanceof CombinedRoom || isMulti,
                        span: innerRoom.spans[orientation]
                    });*/
                    if (innerRoom instanceof CombinedRoom) {
                        spans = spans.concat(this.getSpansByOrientation(orientation, innerRoom.rooms, true));
                    } else {
                        let innerSpan = innerRoom.spans[orientation];
                        spans.push({multi: isMulti, span: innerSpan});
                    }
                }

            } else {
                spans.push({multi: isMulti, span: span});
            }
        }

        return spans;
    }

    sortRoomsByOrientation(orientation, rooms) {
        //room.bounds.min[orientation]; - Relevante Eigenschaft

        //Erst in die Richtung entgegen der Orientierung sortieren dann entlang der Orientierung
        if (orientation === "x") {
            rooms.sort(compareByZ).sort(compareByX);
        } else {
            rooms.sort(compareByX).sort(compareByZ);

        }

        return rooms;

        function compareByX(a, b) {
            return a.bounds.min.x - b.bounds.min.x;
        }

        function compareByZ(a, b) {
            return a.bounds.min.z - b.bounds.min.z;
        }
    }

    /**
     * Sucht zu jeder Spannweite eine passende Deckenstärke heraus abhängig von Einfeld/Mehrfeld.
     * Zurückgegeben wird die Mindeststärke des Dachs (größter gefundener Wert)
     * @param spans
     * @returns {float, null}
     */
    getMinimalRoofThickness(spans) {
        let thickness = 0;
        let singleValues = [...RoofThicknessSingle].reverse();
        let multiValues = [...RoofThicknessMulti].reverse();

        for (let span of spans) {
            let roofThicknesses = span.multi ? multiValues : singleValues;
            let currentThickness = this.getThicknessForSpan(span.span, roofThicknesses, span.multi);
            if (currentThickness > thickness) {
                thickness = currentThickness;
            }
        }

        if (thickness === null) {
            console.error("Zur Dachorientierung konnte keine Dachstärke bestimmt werden");
        }
        return thickness;
    }

    /**
     * Bestimmt zu einer Spanweite und den relevanten Dachstärken mit maximaler Spannweite den passenden Wert aus.
     * @param span
     * @param roofThicknesses
     * @returns {int, null}
     */
    getThicknessForSpan(span, roofThicknesses, isMulti) {
        for (let i = 0; i < roofThicknesses.length; i++) {
            let currentThickness = roofThicknesses[i];
            if (span > currentThickness.maxSpan && i !== 0) {
                return roofThicknesses[i - 1].thickness;
            }
            if (i === roofThicknesses.length - 1 && span < currentThickness.maxSpan) {
                if (!isMulti) {
                    return 100;
                }
                return currentThickness.thickness;
            }
        }

        console.error("Zur Spannweite konnte keine Dachstärke bestimmt werden");
        return null;
    }

    setRoofConfig() {
        //Entscheidung welche Orientierung genutzt wird
        if (this.xValid && this.zValid) {
            //Wenn beide gültig sind hat die kleinere Deckenstärke Prio
            if (this.xThickness < this.zThickness) {
                this.minimalThickness = this.xThickness;
                this.orientation = RoofOrientation.XAxis;
            } else if (this.xThickness > this.zThickness) {
                this.minimalThickness = this.zThickness;
                this.orientation = RoofOrientation.ZAxis;
            } else if (this.xThickness === this.zThickness) {
                //Wenn die Deckenstärke gleich ist wird die kleinste Einfeldträger Spannweite priorisiert
                let xSpan = this.xSpans[0] ? this.xSpans[0].span : undefined;
                let zSpan = this.zSpans[0] ? this.zSpans[0].span : undefined;
                if (xSpan === undefined && zSpan === undefined) {
                    throw new Error(t("roof_span_unknown"));
                }
                if (zSpan === undefined || (xSpan !== undefined && xSpan < zSpan)) {
                    this.minimalThickness = this.xThickness;
                    this.orientation = RoofOrientation.XAxis;
                } else {
                    this.minimalThickness = this.zThickness;
                    this.orientation = RoofOrientation.ZAxis;
                }
            }
        } else if (this.xValid && !this.zValid) {
            //Wenn nur x gültig ist
            this.minimalThickness = this.xThickness;
            this.orientation = RoofOrientation.XAxis;
        } else if (this.zValid && !this.xValid) {
            //Wenn nur z gültig ist
            this.minimalThickness = this.zThickness;
            this.orientation = RoofOrientation.ZAxis;
        }
    }
}
